/**
 * English (base) translation dictionary.
 * All keys referenced by form components live here.
 * Use {variable} for interpolation placeholders.
 */
const en = {
  // ─── Header ───
  'header.buy': 'Buy',
  'header.sell': 'Sell',

  // ─── SpendCard ───
  'spend.label': 'You spend',
  'spend.placeholderFiat': '250',
  'spend.placeholderCrypto': '0.01',

  // ─── ReceiveCard ───
  'receive.label': 'You get',
  'receive.fetchingRate': 'Fetching best rate...',

  // ─── PaymentCard ───
  'payment.label': 'Payment method',
  'payment.select': 'Select',
  'payment.noneAvailable': 'No payment methods for this pair',

  // ─── ProviderCard ───
  'provider.label': 'By',
  'provider.select': 'Select',
  'provider.unavailable': 'Unavailable',
  'provider.unavailablePayment': 'Payment method not supported',
  'provider.unavailableCountry': 'Region not supported',
  'provider.unavailableCurrency': 'Currency not supported',
  'provider.unavailableNetwork': 'Network not supported',

  // ─── WalletCard ───
  'wallet.label': 'Receiving wallet address',
  'wallet.placeholderBuy': 'Enter your receiving address',
  'wallet.placeholderSell': 'Enter your sending address',
  'wallet.validating': 'Validating address...',
  'wallet.invalid': 'Enter a valid wallet address',
  'wallet.invalidServer': 'Invalid wallet address',

  // ─── CtaButton ───
  'cta.buy': 'Buy {crypto}',
  'cta.sell': 'Sell {crypto}',
  'cta.gettingPrice': 'Getting best price...',
  'cta.processing': 'Processing...',

  // ─── Footer ───
  'footer.poweredBy': 'Powered by ',
  'footer.brand': 'NowRamp',

  // ─── Rate Breakdown ───
  'rate.title': 'Rate breakdown',
  'rate.close': 'Close',
  'rate.updatesIn': 'Rate updates in 00:{seconds}',
  'rate.noQuote': 'No quote available',
  'rate.exchangeRate': 'Exchange rate',
  'rate.processingFee': 'Processing fee',
  'rate.networkFee': 'Network fee',
  'rate.provider': 'Provider',
  'rate.updatesInLabel': 'Rate updates in',
  'rate.networkFeeIncl': 'Network fee incl.',
  'rate.rateTooltip': 'As the market is highly volatile, we can fix the exchange rate for no longer than 30 seconds.',
  'rate.networkFeeTooltip': 'Includes a fee charged by blockchain to execute the transaction.',
  'rate.feeIncluded': '{percentage}% incl.',

  // ─── Toast ───
  'toast.orderCreated': 'Order created',
  'toast.redirecting': 'Redirecting to checkout...',
  'toast.orderFailed': 'Order failed',
  'toast.couldNotCreate': 'Could not create order',
  'toast.quoteError': 'Quote error',
  'toast.noProviders': 'No providers',
  'toast.somethingWrong': 'Something went wrong',

  // ─── Form errors ───
  'error.amountZero': 'Amount should be more than 0',
  'error.minAmountFiat': 'Minimum amount is {min} {currency}',
  'error.minAmountCrypto': 'Minimum amount is {min} {currency}',
  'error.noProviders': 'No providers available for this combination',
  'error.currencyUnavailableBuy': 'This currency is not available for buy orders',
  'error.currencyUnavailableSell': 'This currency is not available for sell orders',

  // ─── Parity banner ───
  'parity.info': 'You will be charged for {receiveAmount} {crypto}, the rest up to {spendAmount} {fiat} will be credited to your {partner} account',

  // ─── Redirect Screen ───
  'redirect.secureBadge': 'Secure payment',
  'redirect.title': 'Continue Payment',
  'redirect.description': "You'll be redirected to {gateway} to complete your payment.",
  'redirect.countdown': 'Redirecting in {seconds}...',
  'redirect.openTab': 'Open ACH in new tab',
  'redirect.cancel': 'Cancel',
  'redirect.tabHelper': 'If the new tab has not opened, click the button above.',

  // ─── Checkout Step ───
  'checkout.noOrder': 'No order found',
  'checkout.goBack': 'Go Back',
  'checkout.expired': 'Checkout expired',
  'checkout.expiredDesc': 'The session has expired. Please try again.',
  'checkout.startOver': 'Start over',
  'checkout.cancel': 'Cancel',
  'checkout.loading': 'Loading checkout...',
  'checkout.iframeTitle': 'Provider Checkout',
  'checkout.widgetTitle': 'Payment Checkout',
  'checkout.unknownMethod': 'Unknown checkout method',

  // ─── Processing Step ───
  'processing.default': 'Processing your order...',
  'processing.pending': 'Waiting for payment confirmation...',
  'processing.active': 'Processing your transaction...',
  'processing.generic': 'Processing...',
  'processing.checkingStatus': 'Checking status...',
  'processing.orderId': 'Order ID',
  'processing.provider': 'Provider',
  'processing.amount': 'Amount',
  'processing.receiving': 'Receiving',
  'processing.network': 'Network',
  'processing.toAddress': 'To Address',
  'processing.usuallyMinutes': 'This usually takes a few minutes.',
  'processing.safeToClose': "You can safely close this page - we'll send you an email when complete.",

  // ─── Complete Step ───
  'complete.buyTitle': 'Purchase Complete!',
  'complete.sellTitle': 'Sale Complete!',
  'complete.buyDesc': 'Your {crypto} has been sent to your wallet.',
  'complete.sellDesc': 'Your {fiat} payout is being processed.',
  'complete.amountBuy': 'Amount Received',
  'complete.amountSell': 'Amount Sold',
  'complete.paidBuy': 'Amount Paid',
  'complete.paidSell': 'Payout Amount',
  'complete.network': 'Network',
  'complete.wallet': 'Wallet',
  'complete.transaction': 'Transaction',
  'complete.provider': 'Provider',
  'complete.orderId': 'Order ID',
  'complete.completed': 'Completed',
  'complete.viewExplorer': 'View on Explorer',
  'complete.done': 'Done',

  // ─── Error Step ───
  'errorStep.failed': 'Payment Failed',
  'errorStep.cancelled': 'Order Cancelled',
  'errorStep.expired': 'Order Expired',
  'errorStep.refunded': 'Order Refunded',
  'errorStep.unknown': 'Something Went Wrong',
  'errorStep.failedBuy': 'Your payment could not be processed. Please try again or use a different payment method.',
  'errorStep.failedSell': 'Your sale could not be processed. Please try again.',
  'errorStep.cancelledBuy': 'Your order was cancelled. No payment was processed.',
  'errorStep.cancelledSell': 'Your sale was cancelled. No crypto was transferred.',
  'errorStep.expiredBuy': 'Your order has expired. Please start a new purchase.',
  'errorStep.expiredSell': 'Your order has expired. Please start a new sale.',
  'errorStep.refundedBuy': 'Your payment has been refunded. The funds should appear in your account within 5-10 business days.',
  'errorStep.refundedSell': 'Your crypto has been returned. The funds should appear in your wallet shortly.',
  'errorStep.genericDesc': 'An unexpected error occurred. Please try again.',
  'errorStep.orderId': 'Order ID',
  'errorStep.tryAgain': 'Try Again',
  'errorStep.startOver': 'Start Over',
  'errorStep.return': 'Return',
  'errorStep.needHelp': 'Need help? Contact ',
  'errorStep.reference': 'Reference: {orderId}',

  // ─── Confirm Step ───
  'confirm.buyTitle': 'Confirm Purchase',
  'confirm.sellTitle': 'Confirm Sale',
  'confirm.loading': 'Loading...',
  'confirm.provider': 'Provider',
  'confirm.youPay': 'You pay',
  'confirm.youSell': 'You sell',
  'confirm.youReceive': 'You receive',
  'confirm.wallet': 'Wallet',
  'confirm.network': 'Network',
  'confirm.refundAddress': 'Refund address',
  'confirm.parityNote': 'Charged amount from provider may differ. The rest up to {amount} {crypto} will be credited to your {partner} account.',
  'confirm.processing': 'Processing...',
  'confirm.confirmPay': 'Confirm & Pay',
  'confirm.confirmSell': 'Confirm & Sell',
  'confirm.payout': '{currency} payout',
  'confirm.cryptoOnNetwork': '{crypto} on {network}',

  // ─── Payment method short names ───
  'payment.name.card': 'Card',
  'payment.name.bankTransfer': 'Bank Transfer',
  'payment.name.ach': 'ACH',

  // ─── Modals ───
  'modal.selectFiat': 'Select fiat currency',
  'modal.selectCrypto': 'Select crypto currency',
  'modal.choosePayment': 'Choose payment method',
  'modal.selectCountry': 'Select your country',
  'modal.selectLanguage': 'Select language',
  'modal.popular': 'Popular',
  'modal.recommended': 'Recommended',
  'modal.noCurrencies': 'No currencies found',
  'modal.noPaymentMethods': 'No payment methods found',
  'modal.noCountries': 'No countries found',
  'modal.noLanguages': 'No languages found',

  // ─── ChooseRampModal ───
  'modal.chooseRamp': 'Choose Ramp',
  'modal.bestPrice': 'Best price',
  'modal.noQuotes': 'No quotes available for this request',
  'modal.noProvidersFound': 'No providers found',

  // ─── PaymentMethodSelector (legacy) ───
  'payment.unavailableFor': 'No payment methods available for {currency}',

  // ─── Skeleton ───
  'skeleton.poweredBy': 'Powered by NowRamp',

  // ─── ConfirmStep (catch fallback) ───
  'error.createOrderFailed': 'Failed to create order',

  // ─── ErrorStep aria-label fallback ───
  'errorStep.returnToMerchant': 'Return to merchant',

  // ─── Settings ───
  'settings.location': 'Location',
  'settings.darkMode': 'Dark Mode',
  'settings.privacy': 'Privacy policy',
  'settings.terms': 'Terms of use',
  'settings.language': 'Language',
} as const;

export type TranslationKey = keyof typeof en;
export type TranslationDict = Record<TranslationKey, string>;
export default en;
